/* @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Button, Checkbox, CheckboxGroup, FullscreenDialog } from '@coursera/cds-core';

import useDegreeFilterTracker from 'bundles/premium-hub/components/degrees/useDegreeFilterTracker';
import type { DegreeFilterState } from 'bundles/premium-hub/components/degrees/useMultiSelectDegreeFilterStateManager';
import type { PremiumHubPageType } from 'bundles/premium-hub/constants';
import { HubPages, REGIONAL_CATEGORIES } from 'bundles/premium-hub/constants';
import { getNameFromProductVariant } from 'bundles/premium-hub/utils';

import _t from 'i18n!nls/premium-hub';

type Props = {
  open: boolean;
  handleClose: () => void;
  degreeFilterState: DegreeFilterState;
  categoryName?: string;
  currentPageType?: PremiumHubPageType;
};

const styles = {
  dialogContainer: css`
    hr {
      display: none;
    }
  `,
  dialogHeadingGroup: css`
    padding: var(--cds-spacing-100) var(--cds-spacing-200) var(--cds-spacing-300) var(--cds-spacing-200) !important;
  `,
  dialogContent: css`
    display: flex;
    flex-flow: column;
    gap: var(--cds-spacing-300);
  `,
  actionContainer: css`
    display: flex;
    flex-flow: row wrap;
    gap: var(--cds-spacing-200);
  `,
};

const handleChange = (selectedValue: string[], e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
  let selectedValueMobile = [...selectedValue];
  const value = e.target.value;
  if (checked) {
    if (!selectedValueMobile.includes(value)) {
      selectedValueMobile.push(value);
    }
  } else {
    selectedValueMobile = selectedValueMobile.filter((_) => _ !== value);
  }
  return selectedValueMobile;
};

const DegreeFilterChipDropdownsMobile = ({
  open,
  handleClose,
  degreeFilterState,
  categoryName,
  currentPageType,
}: Props) => {
  const trackPerformSearch = useDegreeFilterTracker(degreeFilterState);
  const { Header, HeadingGroup, Content, Actions } = FullscreenDialog;

  const isLevelHub = currentPageType === HubPages.ProductVariantHubPage;
  const isCategoryHub = currentPageType === HubPages.DegreesCategoryHubPage;
  const isRegional = categoryName && REGIONAL_CATEGORIES.includes(categoryName);

  const {
    setSelectedProductVariant,
    setSelectedDomain,
    setSelectedCost,
    setSelectedDuration,
    setSelectedOpenContentAvailability,
    setSubmittedProductVariant,
    setSubmittedDomain,
    setSubmittedCostBucket,
    setSubmittedDurationBucket,
    setSubmittedOpenContentAvailabilityBucket,
    availableProductVariantFilters = [],
    availableDomainFilters = [],
    availableCostFilters = [],
    availableDurationFilters = [],
    availableOpenContentAvailabilityFilters = [],
    selectedProductVariantValue = [],
    selectedDomainValue = [],
    selectedCostValue = [],
    selectedDurationValue = [],
    selectedOpenContentAvailabilityValue = [],
    submittedDomain,
    submittedProductVariant,
    submittedCostBucket,
    submittedDurationBucket,
    submittedOpenContentAvailabilityBucket,
    clearAll,
  } = degreeFilterState;

  const handleCloseDialog = () => {
    setSelectedDomain(submittedDomain);
    setSelectedProductVariant(submittedProductVariant);
    setSelectedCost(submittedCostBucket);
    setSelectedDuration(submittedDurationBucket);
    setSelectedOpenContentAvailability(submittedOpenContentAvailabilityBucket);
    handleClose();
  };

  const filterNums =
    selectedProductVariantValue.length +
    selectedDomainValue.length +
    selectedCostValue.length +
    selectedDurationValue.length +
    selectedOpenContentAvailabilityValue.length;

  return (
    <FullscreenDialog
      open={open}
      onClose={handleCloseDialog}
      aria-describedby="degreeFiltersRedesignDialogContent"
      sticky="actions"
      css={styles.dialogContainer}
      data-testid="degree-filters-redesign-dialog"
    >
      <Header />
      <HeadingGroup heading={_t('All Filters')} css={styles.dialogHeadingGroup} />
      <Content id="degreeFiltersRedesignDialogContent" css={styles.dialogContent}>
        {!isLevelHub && (
          <CheckboxGroup
            label={_t('Degree Level')}
            value={selectedProductVariantValue}
            onChange={(e, checked) => {
              const values = handleChange(selectedProductVariantValue, e, checked);
              const valueStr = values.length === 0 ? '' : values.join(',');
              trackPerformSearch('productVariant', values);
              setSelectedProductVariant(valueStr);
              setSubmittedProductVariant(valueStr);
            }}
          >
            {availableProductVariantFilters.map((productVariant) => (
              <Checkbox key={productVariant} label={getNameFromProductVariant(productVariant)} value={productVariant} />
            ))}
          </CheckboxGroup>
        )}
        {(!isCategoryHub || isRegional) && (
          <CheckboxGroup
            label={_t('Area of Study')}
            value={selectedDomainValue}
            onChange={(e, checked) => {
              const values = handleChange(selectedDomainValue, e, checked);
              const valueStr = values.length === 0 ? '' : values.join(',');
              trackPerformSearch('domain', values);
              setSelectedDomain(valueStr);
              setSubmittedDomain(valueStr);
            }}
          >
            {availableDomainFilters.map((domain) => (
              <Checkbox key={domain.id} label={domain.name} value={domain.id} />
            ))}
          </CheckboxGroup>
        )}
        <CheckboxGroup
          label={_t('Cost')}
          value={selectedCostValue}
          onChange={(e, checked) => {
            const values = handleChange(selectedCostValue, e, checked);
            const valueStr = values.length === 0 ? '' : values.join(',');
            trackPerformSearch('cost', values);
            setSelectedCost(valueStr);
            setSubmittedCostBucket(valueStr);
          }}
        >
          {availableCostFilters.map(({ id, name }) => (
            <Checkbox key={id} label={name} value={id} />
          ))}
        </CheckboxGroup>
        <CheckboxGroup
          label={_t('Duration')}
          value={selectedDurationValue}
          onChange={(e, checked) => {
            const values = handleChange(selectedDurationValue, e, checked);
            const valueStr = values.length === 0 ? '' : values.join(',');
            trackPerformSearch('duration', values);
            setSelectedDuration(valueStr);
            setSubmittedDurationBucket(valueStr);
          }}
        >
          {availableDurationFilters.map(({ id, name }) => (
            <Checkbox key={id} label={name} value={id} />
          ))}
        </CheckboxGroup>
        <CheckboxGroup
          label={_t('Start with open content')}
          value={selectedOpenContentAvailabilityValue}
          onChange={(e, checked) => {
            const values = handleChange(selectedOpenContentAvailabilityValue, e, checked);
            const valueStr = values.length === 0 ? '' : values.join(',');
            trackPerformSearch('openContentAvailability', values);
            setSelectedOpenContentAvailability(valueStr);
            setSubmittedOpenContentAvailabilityBucket(valueStr);
          }}
        >
          {availableOpenContentAvailabilityFilters.map(({ id, name }) => (
            <Checkbox key={id} label={name} value={id} />
          ))}
        </CheckboxGroup>
      </Content>

      <Actions css={styles.actionContainer}>
        <Button variant="primary" onClick={handleClose}>
          {_t('View')}
        </Button>

        <Button variant="ghost" onClick={clearAll} disabled={!filterNums}>
          {_t('Clear all')}
        </Button>
      </Actions>
    </FullscreenDialog>
  );
};

export default DegreeFilterChipDropdownsMobile;
